// ImportSheet — bottom sheet for recipe import (Phase 2).
// Entry: + Add recipe button in My Recipes section.
// Pipeline: Paste URL / Type text → parse → correction grid → save.
import { useState, useEffect, useRef, useCallback, Fragment } from "react";
import * as api from "../../api.js";
import { C } from "../theme.js";
import { getRecipeSourceDisplayName } from "../utils/displayNames.js";

// ── Locked copy (D4) ──────────────────────────────────────────────────────────
const COPY = {
  pasteHelper:    "Paste a recipe link or the recipe text. We'll pull out the ingredients so you can plan and shop. The instructions stay with the original site.",
  manualFallback: "Recipe page not importing? Paste the recipe text manually and we'll extract the ingredients from that.",
  firstTimeBanner:"Quick check before saving — we pulled the ingredients out of the recipe. Edit anything that looks wrong, then Save. (We don't copy the cooking instructions or photos — those stay with the original site.)",
  saveSuccess:    "Saved to your recipes. We kept the ingredients; the rest stays where it belongs.",
  blocklisted:    (domain) => `NomLoop already prices ingredients from ${domain}. Paste the recipe text below and we'll do the rest.`,
  robotsDisallow: (site) => `${site} prefers we don't fetch from them. Copy the recipe text and paste it below.`,
  llmFail:        "Hm, we couldn't read this one cleanly. Edit the ingredients below — we've kept your text.",
  quotaExceeded:  (n) => `You've imported ${n} recipes today, which is our daily limit during the beta. Try again tomorrow, or paste ingredients manually below.`,
  budgetFull:     "Our monthly recipe-parser budget is full — we'd rather pause than overspend. Paste the ingredients manually below and they'll save normally.",
  authRequired:   "Sign in again to import recipes. Your recipe link is fine.",
  importUnavailable: "Recipe import isn't enabled for this account yet.",
  sitesHelpTitle: "Which sites work?",
  sitesHelp: "URL import works on many recipe sites, but some block automatic import. Manual paste always works.",
};

const LOAD_PHASES = [
  "Reading recipe...",
  "Working out the ingredients...",
  "Almost there — this one's a bit messy...",
];

const LOW_CONF = 0.6;
let _rowSeq = 0;

function rowId() { return ++_rowSeq; }

function initRows(ingredients) {
  return ingredients.map(ing => ({
    id:         rowId(),
    qty:        ing.qtyText ?? ing.qty_text ?? niceQty(ing.qty),
    qtyText:    ing.qtyText ?? ing.qty_text ?? "",
    unit:       ing.unit  ?? "",
    name:       ing.name  ?? "",
    notes:      ing.notes ?? "",
    rawText:    ing.rawText ?? ing.raw_text ?? "",
    section:    ing.section ?? ing.section_title ?? null,
    confidence: ing.confidence ?? 1,
    reviewReason: ing.reviewReason ?? null,
    confirmed:  !ing.reviewReason && (ing.confidence ?? 1) >= LOW_CONF,
  }));
}

function ambersIn(rows) {
  return rows.filter(r => !r.confirmed && (r.confidence < LOW_CONF || r.reviewReason));
}

function validServings(value) {
  const n = Number(value);
  return Number.isInteger(n) && n >= 1 && n <= 20;
}

const QTY_FRACTIONS = {
  "½": 0.5,
  "⅓": 1 / 3,
  "⅔": 2 / 3,
  "¼": 0.25,
  "¾": 0.75,
  "⅛": 0.125,
  "⅜": 0.375,
  "⅝": 0.625,
  "⅞": 0.875,
};

// Reverse map: rounded decimal string → unicode fraction
const DECIMAL_TO_FRAC = new Map(
  Object.entries(QTY_FRACTIONS).map(([sym, val]) => [String(Number(val.toFixed(3))), sym])
);

/** Display a numeric qty as a nice fraction string. e.g. 0.333 → "⅓", 1.5 → "1½" */
function niceQty(qty) {
  if (qty == null) return "";
  const num = Number(qty);
  if (!Number.isFinite(num)) return String(qty);
  const whole = Math.floor(num);
  const frac  = Number((num - whole).toFixed(3));
  const sym   = DECIMAL_TO_FRAC.get(String(frac));
  if (sym && whole > 0) return `${whole}${sym}`;
  if (sym)              return sym;
  if (frac === 0)       return String(whole);
  return String(qty);
}

function parseQtyForSave(value) {
  const text = String(value || "").trim();
  if (!text) return null;
  const first = text.split(/\s+(?:to|-|–|—)\s+/i)[0].trim();
  if (QTY_FRACTIONS[first] != null) return Number(QTY_FRACTIONS[first].toFixed(3));
  const fraction = first.match(/^(\d+)\/(\d+)$/);
  if (fraction) {
    const den = Number(fraction[2]);
    return den ? Number((Number(fraction[1]) / den).toFixed(3)) : null;
  }
  const mixed = first.match(/^(\d+(?:\.\d+)?)\s*([½⅓⅔¼¾⅛⅜⅝⅞])$/);
  if (mixed) return Number((Number(mixed[1]) + QTY_FRACTIONS[mixed[2]]).toFixed(3));
  const n = Number(first);
  return Number.isFinite(n) ? n : null;
}

function qtyTextForSave(row) {
  const text = String(row.qty || "").trim();
  if (!text) return null;
  if (row.qtyText && text === row.qtyText) return row.qtyText;
  return /\s+(?:to|-|–|—)\s+/.test(text) ? text : null;
}

// ── Component ─────────────────────────────────────────────────────────────────

// Loose URL key for duplicate detection: lower-case host+path, drop protocol,
// leading "www.", query, hash, and trailing slash. Good enough to catch the
// same recipe page pasted twice without over-matching.
export function urlMatchKey(url) {
  if (!url || typeof url !== "string") return null;
  try {
    const u = new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`);
    const host = u.host.replace(/^www\./i, "").toLowerCase();
    const path = u.pathname.replace(/\/+$/, "").toLowerCase();
    return `${host}${path}`;
  } catch {
    return null;
  }
}

export function importInputType(rawInput) {
  const text = String(rawInput || "").trim();
  if (!text) return "text";
  if (/\s/.test(text)) return "text";

  const candidate = /^https?:\/\//i.test(text) ? text : `https://${text}`;
  try {
    const url = new URL(candidate);
    if (!["http:", "https:"].includes(url.protocol)) return "text";
    const host = url.hostname.toLowerCase();
    if (host === "localhost" || host.includes(".")) return "url";
  } catch {}
  return "text";
}

function normalizeImportPayload(rawInput, type) {
  const text = String(rawInput || "").trim();
  if (type === "url" && !/^https?:\/\//i.test(text)) return `https://${text}`;
  return text;
}

export function ImportSheet({ open, onClose, onSaved, recipeLibrary = [], onOpenExisting }) {
  const [tab, setTab]               = useState("paste");
  const [payload, setPayload]       = useState("");
  const [phase, setPhase]           = useState("idle");  // idle | loading | correction | saving | saved | duplicate
  // Duplicate state — shared between URL pre-parse guard and save-time fingerprint match
  const [dupMatch, setDupMatch]     = useState(null);    // { id, title } of the existing recipe
  const [dupReason, setDupReason]   = useState(null);    // "url" (pre-parse) | "save" (fingerprint)
  const [copyUsed, setCopyUsed]     = useState(false);   // disable "Save as new copy" after first use
  const [loadStep, setLoadStep]     = useState(0);
  const [error, setError]           = useState(null);    // { message, canPasteManually }
  const [draft, setDraft]           = useState(null);    // parsed metadata
  const [savedFailedUrl, setSavedFailedUrl] = useState(null); // URL preserved after fetch_failed
  const [editTitle, setEditTitle]   = useState("");
  const [editServings, setEditServings] = useState("");
  const [rows, setRows]             = useState([]);
  const [isMobile, setIsMobile]     = useState(() => typeof window !== "undefined" && window.innerWidth < 768);
  const [bannerDismissed, setBannerDismissed] = useState(
    () => typeof window !== "undefined" && localStorage.getItem("nomloop.import_banner_seen") === "1"
  );
  const [showSitesHelp, setShowSitesHelp] = useState(false);

  const abortRef   = useRef(null);
  const parseInFlightRef = useRef(false);
  const saveInFlightRef  = useRef(false);   // synchronous double-submit guard for save
  const textareaRef = useRef(null);
  const startTimeRef = useRef(0);

  // Responsive listener
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  // Loading phase timers
  useEffect(() => {
    if (phase !== "loading") return;
    const t1 = setTimeout(() => setLoadStep(1), 2000);
    const t2 = setTimeout(() => setLoadStep(2), 6000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [phase]);

  // Reset on open
  useEffect(() => {
    if (open) {
      setTab("paste"); setPayload(""); setPhase("idle"); setLoadStep(0);
      setError(null); setDraft(null); setRows([]);
      setEditTitle(""); setEditServings(""); setSavedFailedUrl(null);
      setShowSitesHelp(false); setDupMatch(null); setDupReason(null); setCopyUsed(false);
    }
  }, [open]);

  // Focus textarea when idle
  useEffect(() => {
    if (open && phase === "idle") textareaRef.current?.focus();
  }, [open, phase]);

  // Keyboard shortcuts (when in correction phase)
  useEffect(() => {
    if (phase !== "correction") return;
    function handler(e) {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
      if (e.key === "k" || e.key === "K") {
        setRows(prev => {
          const firstAmber = prev.find(r => !r.confirmed && r.confidence < LOW_CONF);
          if (!firstAmber) return prev;
          return prev.map(r => r.id === firstAmber.id ? { ...r, confirmed: true } : r);
        });
      }
    }
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [phase]);

  // Cmd/Ctrl+Enter to save
  useEffect(() => {
    if (phase !== "correction") return;
    function handler(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        const ambers = ambersIn(rows);
        if (ambers.length === 0) handleSave();
      }
    }
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, rows]);

  async function handleParse(force = false) {
    if (parseInFlightRef.current) return;
    if (!payload.trim()) return;
    const trimmed = payload.trim();
    const parseType = importInputType(trimmed);
    const isUrl = parseType === "url";
    const parsePayload = normalizeImportPayload(trimmed, parseType);

    // Pre-parse duplicate guard: if this URL is already in the library, warn
    // BEFORE spending an LLM parse. User can open the saved copy or import again.
    if (!force && isUrl) {
      const key = urlMatchKey(parsePayload);
      const existing = key && recipeLibrary.find(r => urlMatchKey(r.source_url) === key);
      if (existing) {
        setDupMatch({ id: existing.id, title: existing.title });
        setDupReason("url");
        setPhase("duplicate");
        return;
      }
    }

    parseInFlightRef.current = true;
    abortRef.current = new AbortController();
    setPhase("loading");
    setLoadStep(0);
    setError(null);
    startTimeRef.current = Date.now();

    try {
      const result = await api.parseImport(
        { type: parseType, payload: parsePayload },
        { signal: abortRef.current.signal }
      );
      setDraft(result);
      setEditTitle(result.title ?? "");
      setEditServings(result.servings ? String(result.servings) : "");
      setRows(initRows(result.ingredients ?? []));
      setPhase("correction");
    } catch (err) {
      if (err.name === "AbortError") return;
      const msg = parseErrorMessage(err, payload);
      setError(msg);
      setPhase("idle");
    } finally {
      parseInFlightRef.current = false;
      abortRef.current = null;
    }
  }

  function handleCancel() {
    parseInFlightRef.current = false;
    abortRef.current?.abort();
    setPhase("idle");
    setLoadStep(0);
  }

  function switchToManualPaste() {
    const trimmed = payload.trim();
    const isUrl = importInputType(trimmed) === "url";
    if (isUrl) setSavedFailedUrl(trimmed);
    setPayload(isUrl ? "" : payload);
    setTab("type");
    setError(null);
    setPhase("idle");
    setTimeout(() => textareaRef.current?.focus(), 50);
  }

  async function handleSave(saveAsCopy = false) {
    // Defensive: never let a React event / DOM node leak into the JSON payload.
    // Coercing to a strict boolean kills any circular structure passed by mistake.
    saveAsCopy = saveAsCopy === true;
    // Synchronous guard: phase state is async, so two rapid triggers (button +
    // Cmd-Enter, or a double tap) could both pass a phase check and fire two
    // saves — the first succeeds, the second 429s, and the user sees a false
    // "Too many saves" even though the recipe saved. The ref blocks the second
    // call in the same tick.
    if (saveInFlightRef.current || phase === "saving") return;
    if (ambersIn(rows).length > 0 || !validServings(editServings)) return;
    saveInFlightRef.current = true;
    setPhase("saving");

    const corrections = rows
      .filter(r => r.confidence < LOW_CONF)
      .map(r => ({ originalName: r.name, correctedName: r.name, originalUnit: r.unit || null, correctedUnit: r.unit || null }));

    const ingredientPayload = rows.map(r => ({
      qty:          parseQtyForSave(r.qty),
      qtyText:      qtyTextForSave(r),
      unit:         r.unit || null,
      name:         r.name,
      notes:        r.notes || null,
      section:      r.section || null,
      confidence:   r.confidence,
      rawText:      r.rawText || null,
      reviewReason: r.reviewReason || null,
    }));

    try {
      const saved = await api.saveImport({
        title:           editTitle || "Untitled Recipe",
        servings:        editServings ? parseInt(editServings, 10) : null,
        ingredients:     ingredientPayload,
        sourceDomain:    draft?.sourceDomain ?? null,
        sourceUrl:       draft?.sourceUrl ?? null,
        source_image_url: draft?.source_image_url ?? draft?.sourceImageUrl ?? null,
        cuisine:         draft?.cuisine ?? null,
        chef:            draft?.chef        ?? null,
        publisher:       draft?.publisher   ?? null,
        prepMinutes:     draft?.prepMinutes ?? null,
        cookMinutes:     draft?.cookMinutes ?? null,
        dietary:         Array.isArray(draft?.dietary) ? draft.dietary : [],
        instructions:    Array.isArray(draft?.instructions) ? draft.instructions : [],
        importMethod:    draft?.importMethod ?? "llm",
        corrections,
      }, { saveAsCopy });

      if (saved.duplicate) {
        // Server detected a fingerprint match — show the duplicate panel.
        setDupMatch({ id: saved.existingId, title: saved.existingTitle });
        setDupReason("save");
        setPhase("duplicate");
        return;
      }

      setPhase("saved");
      onSaved?.({
        id:           saved.id,
        title:        editTitle,
        sourceDomain: draft?.sourceDomain,
        sourceUrl:    draft?.sourceUrl ?? null,
        source_image_url: draft?.source_image_url ?? draft?.sourceImageUrl ?? null,
        cuisine:      draft?.cuisine ?? null,
        chef:         draft?.chef        ?? null,
        publisher:    draft?.publisher   ?? null,
        prepMinutes:  draft?.prepMinutes ?? null,
        cookMinutes:  draft?.cookMinutes ?? null,
        dietary:      Array.isArray(draft?.dietary) ? draft.dietary : [],
        instructions: Array.isArray(draft?.instructions) ? draft.instructions : [],
        servings:     editServings ? parseInt(editServings, 10) : null,
        ingredients:  ingredientPayload,
      });
    } catch (err) {
      if (err.status === 429) {
        const retryMsg = err.retryAfter
          ? `Too many saves — please wait ${err.retryAfter}s before trying again.`
          : "Too many saves right now. Please wait a moment and try again.";
        setError({ message: retryMsg, canPasteManually: false });
      } else {
        setError({ message: err.message || "Couldn't save. Please try again.", canPasteManually: false });
      }
      setPhase("correction");
    } finally {
      saveInFlightRef.current = false;
    }
  }

  function dismissBanner() {
    setBannerDismissed(true);
    try { localStorage.setItem("nomloop.import_banner_seen", "1"); } catch {}
  }

  function patchRow(id, field, value) {
    setRows(prev => prev.map(r => r.id === id ? { ...r, [field]: value, confirmed: true } : r));
  }

  function confirmRow(id) {
    setRows(prev => prev.map(r => r.id === id ? { ...r, confirmed: true } : r));
  }

  if (!open) return null;

  const ambers     = ambersIn(rows);
  const servingsMissing = !validServings(editServings);
  const canSave    = ambers.length === 0 && !servingsMissing;
  const saveBtnTxt = servingsMissing
    ? "Set servings first"
    : canSave ? "Save to my recipes" : `Check ${ambers.length} ingredient${ambers.length === 1 ? "" : "s"} first`;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Add recipe"
      onClick={onClose}
      style={{ position:"fixed", inset:0, zIndex:550, display:"flex", alignItems:"flex-end", justifyContent:"center" }}
    >
      {/* Backdrop */}
      <div style={{ position:"absolute", inset:0, background:"rgba(0,0,0,.45)", backdropFilter:"blur(4px)" }} />

      {/* Sheet */}
      <div
        onClick={e => e.stopPropagation()}
        style={{
          position:"relative", background:C.white, borderRadius:"24px 24px 0 0",
          width:"100%", maxWidth: phase === "correction" ? 640 : 480,
          maxHeight:"92dvh", display:"flex", flexDirection:"column",
          boxShadow:"0 -16px 60px rgba(0,0,0,.2)", animation:"slideUp .3s ease",
        }}
      >
        {/* Handle */}
        <div style={{ width:36, height:4, background:C.border, borderRadius:99, margin:"16px auto 0", flexShrink:0 }} />

        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"12px 20px 0", flexShrink:0 }}>
          <div style={{ fontFamily:"Lora,serif", fontWeight:700, fontSize:18, color:C.charcoal }}>
            {phase === "correction" ? (editTitle || "New recipe") : "Add recipe"}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ background:"none", border:"none", cursor:"pointer", fontSize:18, color:C.grey, padding:4, lineHeight:1 }}
          >✕</button>
        </div>

        {/* Scrollable body */}
        <div style={{ overflowY:"auto", flex:1, padding:"16px 20px 24px" }}>

          {/* ── IDLE / ERROR ── */}
          {(phase === "idle") && (
            <>
              {/* Tabs */}
              <div style={{ display:"flex", gap:4, marginBottom:14, background:C.sand, borderRadius:10, padding:3 }}>
                {[["paste","Paste URL or text"],["type","Type manually"]].map(([t,l]) => (
                  <button
                    key={t}
                    onClick={() => { setTab(t); setError(null); if (t === "paste") setSavedFailedUrl(null); }}
                    style={{
                      flex:1, padding:"8px 0", borderRadius:8, border:"none",
                      background: tab===t ? C.white : "transparent",
                      color: tab===t ? C.charcoal : C.grey,
                      fontWeight: tab===t ? 700 : 600,
                      fontSize:13, cursor:"pointer",
                      boxShadow: tab===t ? C.sh : "none",
                      transition:"all .15s",
                    }}
                  >{l}</button>
                ))}
              </div>

              {/* Helper text */}
              <p style={{ fontSize:13, color:C.grey, marginBottom:10, lineHeight:1.5 }}>
                {COPY.pasteHelper}
              </p>
              <button
                type="button"
                onClick={() => setShowSitesHelp(v => !v)}
                aria-expanded={showSitesHelp}
                style={{ display:"inline-flex", marginBottom:10, background:"none", border:"none", padding:0, color:C.green, fontSize:12, fontWeight:700, cursor:"pointer" }}
              >
                {COPY.sitesHelpTitle}
              </button>
              {showSitesHelp && (
                <div style={{ padding:"10px 12px", borderRadius:10, background:C.greenPale, color:C.green, fontSize:12, lineHeight:1.5, marginBottom:12 }}>
                  {COPY.sitesHelp}
                </div>
              )}

              {/* Error banner */}
              {error && (
                <div style={{ padding:"12px 14px", borderRadius:12, background:C.goldPale, color:C.amber, fontSize:13, marginBottom:12, lineHeight:1.5, border:`1px solid ${C.gold}55` }}>
                  {error.message}
                  {error.canPasteManually && tab === "paste" && (
                    <button
                      onClick={switchToManualPaste}
                      style={{ display:"block", width:"100%", marginTop:10, fontWeight:800, color:C.white, background:C.green, border:"none", cursor:"pointer", padding:"10px 14px", borderRadius:99, fontSize:13 }}
                    >Use manual paste instead</button>
                  )}
                </div>
              )}

              {tab === "paste" && !error && (
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, padding:"10px 12px", borderRadius:12, background:C.greenPale, color:C.green, fontSize:12, lineHeight:1.4, marginBottom:12 }}>
                  <span>{COPY.manualFallback}</span>
                  <button
                    type="button"
                    onClick={switchToManualPaste}
                    style={{ flexShrink:0, fontWeight:800, color:C.green, background:C.white, border:`1px solid ${C.greenMid}`, cursor:"pointer", padding:"7px 10px", borderRadius:99, fontSize:12 }}
                  >Use manual paste</button>
                </div>
              )}

              {/* Saved URL reference — shown in "type" tab after a fetch failure */}
              {tab === "type" && savedFailedUrl && (
                <div style={{ fontSize:12, color:C.grey, marginBottom:8, display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
                  <span>Reference:</span>
                  <a
                    href={savedFailedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color:C.green, wordBreak:"break-all" }}
                  >{savedFailedUrl}</a>
                </div>
              )}

              {/* Input */}
              <textarea
                ref={textareaRef}
                value={payload}
                onChange={e => { setPayload(e.target.value); setError(null); }}
                placeholder={tab === "paste" ? "Paste a recipe URL or recipe text here" : "Paste the recipe text here…"}
                rows={5}
                onKeyDown={e => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") handleParse(); }}
                aria-busy={parseInFlightRef.current ? "true" : undefined}
                style={{
                  width:"100%", padding:"12px 14px", borderRadius:12,
                  border:`1.5px solid ${C.border}`, fontSize:14,
                  fontFamily:"inherit", resize:"vertical", outline:"none",
                  color:C.charcoal, background:C.white, lineHeight:1.5,
                }}
              />

              {/* Submit */}
              <button
                onClick={() => handleParse()}
                disabled={!payload.trim() || parseInFlightRef.current}
                style={{
                  marginTop:12, width:"100%", padding:"13px 0",
                  borderRadius:99, border:"none",
                  background: payload.trim() && !parseInFlightRef.current ? C.green : C.greenPale,
                  color: payload.trim() && !parseInFlightRef.current ? C.white : C.grey,
                  fontWeight:700, fontSize:15,
                  cursor: payload.trim() && !parseInFlightRef.current ? "pointer" : "default",
                  transition:"all .15s",
                }}
              >
                {parseInFlightRef.current ? "Extracting…" : tab === "paste" ? "Get ingredients →" : "Extract ingredients →"}
              </button>
            </>
          )}

          {/* ── LOADING ── */}
          {phase === "loading" && (
            <div style={{ textAlign:"center", padding:"32px 0 16px" }}>
              <div aria-live="polite" style={{ fontWeight:700, fontSize:16, color:C.charcoal, marginBottom:10 }}>
                {LOAD_PHASES[loadStep]}
              </div>
              <div style={{ fontSize:28, marginBottom:20, animation:"fadeUp .4s ease" }}>⏳</div>
              <button
                onClick={handleCancel}
                style={{ padding:"10px 24px", borderRadius:99, border:`1.5px solid ${C.border}`, background:C.white, color:C.grey, fontWeight:700, fontSize:13, cursor:"pointer" }}
              >Cancel</button>
            </div>
          )}

          {/* ── CORRECTION GRID ── */}
          {(phase === "correction" || phase === "saving") && (
            <>
              {/* First-time banner (D8) */}
              {!bannerDismissed && (
                <div style={{ display:"flex", gap:10, alignItems:"flex-start", padding:"12px 14px", borderRadius:10, background:C.tealPale, color:C.teal, fontSize:13, marginBottom:14, lineHeight:1.5 }}>
                  <span style={{ fontSize:16, flexShrink:0 }}>ℹ️</span>
                  <div style={{ flex:1 }}>{COPY.firstTimeBanner} <em style={{ opacity:.8 }}>Tip: press K to confirm a row.</em></div>
                  <button onClick={dismissBanner} aria-label="Dismiss" style={{ background:"none", border:"none", cursor:"pointer", color:C.teal, fontSize:15, padding:0, flexShrink:0, lineHeight:1 }}>✕</button>
                </div>
              )}

              {/* Amber count banner */}
              {ambers.length > 0 && (
                <div aria-live="polite" style={{ fontSize:13, color:"#92400E", background:"#FFFBEB", border:"1px solid #FDE68A", borderRadius:8, padding:"8px 12px", marginBottom:12 }}>
                  {ambers.length} ingredient{ambers.length === 1 ? "" : "s"} need a quick check
                </div>
              )}
              {servingsMissing && (
                <div aria-live="polite" style={{ fontSize:13, color:"#92400E", background:"#FFFBEB", border:"1px solid #FDE68A", borderRadius:8, padding:"8px 12px", marginBottom:12 }}>
                  Serving count is missing. Add how many people the original recipe serves before saving.
                </div>
              )}

              {/* Attribution */}
              {draft?.sourceDomain && (
                <div style={{ fontSize:12, color:C.grey, marginBottom:10, fontStyle:"italic" }}>
                  Recipe inspired by <strong>{getRecipeSourceDisplayName(draft.sourceUrl ?? null, draft.sourceDomain)}</strong>. We respect their work — view the full instructions there.
                </div>
              )}

              {/* Title + Servings */}
              <div style={{ display:"flex", gap:10, marginBottom:14 }}>
                <div style={{ flex:1 }}>
                  <label style={{ fontSize:11, fontWeight:700, color:C.grey, display:"block", marginBottom:4 }}>TITLE</label>
                  <input
                    value={editTitle}
                    onChange={e => setEditTitle(e.target.value)}
                    placeholder="Recipe title"
                    style={{ width:"100%", padding:"9px 12px", borderRadius:8, border:`1.5px solid ${C.border}`, fontSize:14, fontFamily:"inherit", outline:"none" }}
                  />
                </div>
                <div style={{ width:80 }}>
                  <label style={{ fontSize:11, fontWeight:700, color:C.grey, display:"block", marginBottom:4 }}>SERVES</label>
                  <input
                    type="number" min="1" max="20"
                    value={editServings}
                    onChange={e => setEditServings(e.target.value)}
                    placeholder="—"
                    aria-invalid={servingsMissing ? "true" : undefined}
                    style={{ width:"100%", padding:"9px 10px", borderRadius:8, border:`1.5px solid ${servingsMissing ? "#F59E0B" : C.border}`, background:servingsMissing ? "#FFFBEB" : C.white, fontSize:14, fontFamily:"inherit", outline:"none" }}
                  />
                </div>
              </div>

              {/* Grid */}
              {isMobile
                ? <MobileCards rows={rows} onPatch={patchRow} onConfirm={confirmRow} />
                : <DesktopTable rows={rows} onPatch={patchRow} onConfirm={confirmRow} />
              }

              {/* Error */}
              {error && phase === "correction" && (
                <div style={{ padding:"10px 14px", borderRadius:10, background:"#FEF2F2", color:"#991B1B", fontSize:13, marginTop:12 }}>
                  {error.message}
                </div>
              )}

              {/* Save */}
              <button
                onClick={canSave ? () => handleSave() : undefined}
                disabled={!canSave || phase === "saving"}
                title={servingsMissing ? "Set the serving count first" : !canSave ? `Check ${ambers.length} ingredient${ambers.length === 1 ? "" : "s"} first` : undefined}
                style={{
                  marginTop:16, width:"100%", padding:"13px 0", borderRadius:99, border:"none",
                  background: canSave && phase !== "saving" ? C.green : C.greenPale,
                  color: canSave && phase !== "saving" ? C.white : C.grey,
                  fontWeight:700, fontSize:15,
                  cursor: canSave && phase !== "saving" ? "pointer" : "not-allowed",
                  transition:"all .15s",
                }}
              >
                {phase === "saving" ? "Saving…" : saveBtnTxt}
              </button>
            </>
          )}

          {/* ── DUPLICATE WARNING ── */}
          {/* One panel for both pre-parse URL match (dupReason="url") and        */}
          {/* save-time fingerprint match (dupReason="save"). Buttons differ by reason. */}
          {phase === "duplicate" && (
            <div style={{ textAlign:"center", padding:"32px 0 16px" }}>
              <div style={{ fontSize:36, marginBottom:14 }}>♻️</div>
              <div style={{ fontWeight:700, fontSize:16, color:C.charcoal, marginBottom:8 }}>
                You've already imported this recipe
              </div>
              <p style={{ fontSize:13, color:C.grey, marginBottom:20, lineHeight:1.5 }}>
                A recipe named &ldquo;{dupMatch?.title || "this recipe"}&rdquo; already exists.
              </p>
              <div style={{ display:"flex", flexDirection:"column", gap:10, alignItems:"center" }}>

                {/* Save as new copy — only available for save-time dups, and only once */}
                {dupReason === "save" && !copyUsed && (
                  <button
                    onClick={() => { setCopyUsed(true); handleSave(true); }}
                    style={{ padding:"12px 32px", borderRadius:99, border:"none", background:C.green, color:C.white, fontWeight:700, fontSize:14, cursor:"pointer" }}
                  >Save as new copy</button>
                )}

                {/* Open the existing saved recipe */}
                {dupMatch?.id && onOpenExisting && (
                  <button
                    onClick={() => onOpenExisting(dupMatch.id)}
                    style={{ padding:"10px 28px", borderRadius:99, border:`1.5px solid ${C.border}`, background:C.white, color:C.charcoal, fontWeight:700, fontSize:13, cursor:"pointer" }}
                  >Open saved recipe</button>
                )}

                {/* URL path: re-run the parse anyway (force=true skips URL guard) */}
                {dupReason === "url" && (
                  <button
                    onClick={() => handleParse(true)}
                    style={{ padding:"10px 28px", borderRadius:99, border:`1.5px solid ${C.border}`, background:C.white, color:C.grey, fontWeight:600, fontSize:13, cursor:"pointer" }}
                  >Import again anyway</button>
                )}

                {/* Cancel — return to correction grid (save) or idle input (url) */}
                <button
                  onClick={() => setPhase(dupReason === "save" ? "correction" : "idle")}
                  style={{ padding:"10px 28px", borderRadius:99, border:"none", background:"none", color:C.grey, fontWeight:600, fontSize:13, cursor:"pointer" }}
                >Cancel</button>
              </div>
            </div>
          )}

          {/* ── SAVED ── */}
          {phase === "saved" && (
            <div style={{ textAlign:"center", padding:"32px 0 16px" }}>
              <div style={{ fontSize:36, marginBottom:14 }}>✅</div>
              <div style={{ fontWeight:700, fontSize:16, color:C.charcoal, marginBottom:8 }}>
                {editTitle || "Recipe"} saved
              </div>
              <p style={{ fontSize:13, color:C.grey, marginBottom:20, lineHeight:1.5 }}>
                {COPY.saveSuccess}
              </p>
              <button
                onClick={onClose}
                style={{ padding:"12px 32px", borderRadius:99, border:"none", background:C.green, color:C.white, fontWeight:700, fontSize:14, cursor:"pointer" }}
              >Done</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Section heading divider ───────────────────────────────────────────────────
// Rendered above the first row of each ingredient sub-section (e.g. "First
// Marination"). Display-only — never editable, never part of the saved rows.
function SectionHeading({ title }) {
  return (
    <div
      role="rowgroup"
      aria-label={title}
      style={{
        gridColumn: "1/-1",
        margin: "12px 0 4px",
        fontSize: 12, fontWeight: 700, letterSpacing: 0.3,
        textTransform: "uppercase", color: C.grey,
        borderBottom: `1px solid ${C.border}`, paddingBottom: 4,
      }}
    >
      {title}
    </div>
  );
}

// True when this row opens a new sub-section relative to the previous row.
function startsSection(rows, idx) {
  const section = rows[idx]?.section || null;
  if (!section) return false;
  const prev = idx > 0 ? (rows[idx - 1]?.section || null) : null;
  return section !== prev;
}

// ── Desktop 4-column table ────────────────────────────────────────────────────
function DesktopTable({ rows, onPatch, onConfirm }) {
  return (
    <div role="grid" aria-label="Ingredients" style={{ width:"100%" }}>
      {/* Header */}
      <div role="row" style={{ display:"grid", gridTemplateColumns:"90px 110px 1fr 1fr", gap:6, marginBottom:4, paddingBottom:4, borderBottom:`1.5px solid ${C.border}` }}>
        {["Qty","Unit","Name","Notes"].map(h => (
          <div key={h} style={{ fontSize:11, fontWeight:700, color:C.grey }}>{h}</div>
        ))}
      </div>
      {rows.map((row, idx) => {
        const isAmber = !row.confirmed && (row.confidence < LOW_CONF || row.reviewReason);
        return (
          <Fragment key={row.id}>
          {startsSection(rows, idx) && <SectionHeading title={row.section} />}
          <div
            role="row"
            aria-invalid={isAmber ? "true" : undefined}
            style={{
              display:"grid", gridTemplateColumns:"90px 110px 1fr 1fr", gap:6,
              alignItems:"center", padding:"4px 0",
              borderLeft: isAmber ? `3px solid #B45309` : "3px solid transparent",
              paddingLeft: isAmber ? 6 : 0,
              marginBottom:4,
            }}
          >
            {isAmber && (
              <div style={{ gridColumn:"1/-1", display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                <span style={{ fontSize:13 }}>❓</span>
                <span style={{ fontSize:12, color:"#92400E", fontWeight:600 }}>{row.reviewReason || "Looks uncertain"}</span>
                {row.rawText && <span title={row.rawText} style={{ fontSize:11, color:C.grey, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>Original: {row.rawText}</span>}
                <button onClick={() => onConfirm(row.id)} style={{ marginLeft:"auto", padding:"3px 10px", borderRadius:99, border:"1px solid #B45309", background:"#FFFBEB", color:"#92400E", fontWeight:700, fontSize:12, cursor:"pointer" }}>Looks right</button>
              </div>
            )}
            {[
              ["qty","text","—"],
              ["unit","text",""],
              ["name","text","ingredient"],
              ["notes","text",""],
            ].map(([field,type,placeholder]) => (
              <input
                key={field}
                type={type}
                value={row[field]}
                placeholder={placeholder}
                title={field === "notes" && row[field] ? row[field] : undefined}
                onChange={e => onPatch(row.id, field, e.target.value)}
                style={{
                  padding:"7px 10px", borderRadius:8,
                  border:`1.5px solid ${isAmber ? "#FDE68A" : C.border}`,
                  fontSize:13, fontFamily:"inherit", outline:"none",
                  background: isAmber ? "#FFFBEB" : C.white,
                  height:36, width:"100%",
                }}
              />
            ))}
          </div>
          </Fragment>
        );
      })}
    </div>
  );
}

// ── Mobile stacked cards ──────────────────────────────────────────────────────
function MobileCards({ rows, onPatch, onConfirm }) {
  return (
    <div role="list" style={{ display:"flex", flexDirection:"column", gap:10 }}>
      {rows.map((row, idx) => {
        const isAmber = !row.confirmed && (row.confidence < LOW_CONF || row.reviewReason);
        return (
          <Fragment key={row.id}>
          {startsSection(rows, idx) && <SectionHeading title={row.section} />}
          <div
            role="listitem"
            aria-invalid={isAmber ? "true" : undefined}
            style={{
              borderRadius:12, border:`1.5px solid ${isAmber ? "#FDE68A" : C.border}`,
              borderLeft: `3px solid ${isAmber ? "#B45309" : C.border}`,
              padding:"12px 14px",
              background: isAmber ? "#FFFBEB" : C.white,
            }}
          >
            {/* Name row */}
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
              {isAmber && <span style={{ fontSize:14, flexShrink:0 }}>❓</span>}
              {isAmber && row.reviewReason && (
                <span style={{ fontSize:12, color:"#92400E", fontWeight:700, flexShrink:0 }}>{row.reviewReason}</span>
              )}
              {isAmber && row.rawText && (
                <span title={row.rawText} style={{ fontSize:11, color:C.grey, flexShrink:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{row.rawText}</span>
              )}
              <input
                type="text"
                value={row.name}
                placeholder="ingredient"
                onChange={e => onPatch(row.id, "name", e.target.value)}
                style={{
                  flex:1, padding:"10px 12px", borderRadius:8,
                  border:`1.5px solid ${isAmber ? "#FDE68A" : C.border}`,
                  fontSize:14, fontFamily:"inherit", outline:"none",
                  background:"transparent", fontWeight:600,
                  height:44,
                }}
              />
              {isAmber && (
                <button onClick={() => onConfirm(row.id)} style={{ flexShrink:0, padding:"0 12px", height:44, borderRadius:99, border:"1px solid #B45309", background:"transparent", color:"#92400E", fontWeight:700, fontSize:13, cursor:"pointer" }}>Looks right</button>
              )}
            </div>
            {/* Qty + Unit */}
            <div style={{ display:"flex", gap:8, marginBottom:8 }}>
              <div style={{ flex:1 }}>
                <label style={{ fontSize:11, fontWeight:700, color:C.grey, display:"block", marginBottom:3 }}>Qty</label>
                <input
                  type="text"
                  value={row.qty}
                  placeholder="—"
                  onChange={e => onPatch(row.id, "qty", e.target.value)}
                  style={{ width:"100%", padding:"10px 12px", borderRadius:8, border:`1.5px solid ${isAmber ? "#FDE68A" : C.border}`, fontSize:14, fontFamily:"inherit", outline:"none", height:44, background:"transparent" }}
                />
              </div>
              <div style={{ flex:1 }}>
                <label style={{ fontSize:11, fontWeight:700, color:C.grey, display:"block", marginBottom:3 }}>Unit</label>
                <input
                  type="text"
                  value={row.unit}
                  placeholder="g, ml, tsp…"
                  onChange={e => onPatch(row.id, "unit", e.target.value)}
                  style={{ width:"100%", padding:"10px 12px", borderRadius:8, border:`1.5px solid ${isAmber ? "#FDE68A" : C.border}`, fontSize:14, fontFamily:"inherit", outline:"none", height:44, background:"transparent" }}
                />
              </div>
            </div>
            {/* Notes */}
            <div>
              <label style={{ fontSize:11, fontWeight:700, color:C.grey, display:"block", marginBottom:3 }}>Notes</label>
              <input
                type="text"
                value={row.notes}
                placeholder=""
                title={row.notes || undefined}
                onChange={e => onPatch(row.id, "notes", e.target.value)}
                style={{ width:"100%", padding:"10px 12px", borderRadius:8, border:`1.5px solid ${isAmber ? "#FDE68A" : C.border}`, fontSize:14, fontFamily:"inherit", outline:"none", height:44, background:"transparent" }}
              />
            </div>
          </div>
          </Fragment>
        );
      })}
    </div>
  );
}

// ── Error message mapper ──────────────────────────────────────────────────────
export function parseErrorMessage(err, originalPayload) {
  const code = err.code || (err.message?.match(/— (\w+)$/)?.[1]);
  const msg  = err.message || "";

  if (msg.includes("blocklisted") || code === "blocklisted") {
    const domain = tryExtractDomain(originalPayload);
    return { message: COPY.blocklisted(domain || "this retailer"), canPasteManually: true };
  }
  if (msg.includes("robots") || code === "robots_disallowed") {
    const domain = tryExtractDomain(originalPayload);
    return { message: COPY.robotsDisallow(domain || "This site"), canPasteManually: true };
  }
  if (err.status === 401 || msg.includes("Authentication required") || msg.includes("Invalid token") || msg.includes("Token expired")) {
    return { message: COPY.authRequired, canPasteManually: false };
  }
  if (err.status === 404) {
    return { message: COPY.importUnavailable, canPasteManually: true };
  }
  if (err.status === 429) {
    return { message: "Import is busy right now. You can still paste the recipe manually.", canPasteManually: true };
  }
  if (msg.includes("timeout") || code === "fetch_timeout" || code === "llm_timeout") {
    return { message: COPY.llmFail, canPasteManually: true };
  }
  if (code === "fetch_failed") {
    return { message: "We couldn't fetch this page. Paste the recipe text instead.", canPasteManually: true };
  }
  if (code === "fetch_too_large") {
    return { message: "That recipe page is too large to import directly. Paste the recipe text manually instead.", canPasteManually: true };
  }
  if (msg.includes("quota") || code === "quota_exceeded") {
    return { message: COPY.quotaExceeded("20"), canPasteManually: false };
  }
  if (msg.includes("budget") || code === "budget_exhausted") {
    return { message: COPY.budgetFull, canPasteManually: true };
  }
  if (code === "llm_refused" || code === "llm_failed" || msg.includes("malformed")) {
    return { message: COPY.llmFail, canPasteManually: true };
  }
  return { message: "Something went wrong. Please try again or paste the text manually.", canPasteManually: true };
}

function tryExtractDomain(text) {
  try { return new URL(text.trim()).hostname.replace(/^www\./, ""); } catch { return null; }
}
